/**
 * Login screen — plan 16 MVP (account wajib via Google sign-in).
 *
 * Web (PWA): Google Identity Services (GIS) renderButton flow.
 *  - Script https://accounts.google.com/gsi/client dimuat dinamis.
 *  - Client ID dari EXPO_PUBLIC_GOOGLE_CLIENT_ID.
 *  - Credential (ID token) ditukar ke session token via signInWithGoogle.
 *  - Setelah login: user lama (punya child) → /home; user baru → /welcome.
 *
 * Native: di luar scope MVP — tampilkan fallback message.
 *
 * Doc: tm-ai-workspace/plan/funpaper-app/16-mvp-pwa-free-launch.md (Task A7)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { AppIcon } from '@/components/app-icon';
import { SegeraHadirModal } from '@/components/premium/SegeraHadirModal';
import { Brand, Spacing } from '@/constants/theme';
import { MISC_ICON } from '@/lib/icons';
import { signInWithGoogle } from '@/lib/api/auth';
import { getActiveChild } from '@/lib/api/children';
import { getAuthToken, isGuestSession } from '@/lib/auth';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID ?? '';

/** Subset GIS API yang dipakai (hindari dependency @types/google-one-tap). */
interface GisIdApi {
  initialize(config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: string;
      theme?: string;
      size?: string;
      shape?: string;
      text?: string;
      width?: number;
      locale?: string;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GisIdApi } };
  }
}

function loadGisScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('gis load failed')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('gis load failed'));
    document.head.appendChild(script);
  });
}

export default function LoginScreen() {
  const router = useRouter();
  const buttonHostRef = useRef<View | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'signing-in'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [signupBonus, setSignupBonus] = useState<{ amount: number } | null>(null);

  const routeAfterLogin = useCallback(async () => {
    try {
      const child = await getActiveChild();
      router.replace((child ? '/home' : '/welcome') as never);
    } catch {
      // Gagal cek child (network) — default ke onboarding; guard data
      // tetap aman karena /home akan error-handle sendiri.
      router.replace('/welcome' as never);
    }
  }, [router]);

  // User yang sudah login tidak perlu lihat layar ini.
  useEffect(() => {
    if (getAuthToken() && !isGuestSession()) {
      void routeAfterLogin();
    }
  }, [routeAfterLogin]);

  const continueAfterSignup = useCallback(() => {
    setSignupBonus(null);
    void routeAfterLogin();
  }, [routeAfterLogin]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!GOOGLE_CLIENT_ID) {
      setErrorMessage('Konfigurasi login belum lengkap. Hubungi tim Funpaper.');
      return;
    }

    let cancelled = false;

    const handleCredential = async (response: { credential?: string }) => {
      if (!response.credential) {
        setErrorMessage('Verifikasi Google gagal. Coba lagi.');
        return;
      }
      setStatus('signing-in');
      setErrorMessage(null);
      try {
        const result = await signInWithGoogle(response.credential);
        if (result.signupBonus) {
          setSignupBonus(result.signupBonus);
          return;
        }
        await routeAfterLogin();
      } catch {
        if (cancelled) return;
        setStatus('ready');
        setErrorMessage('Login gagal. Periksa koneksi lalu coba lagi.');
      }
    };

    loadGisScript()
      .then(() => {
        if (cancelled) return;
        const gis = window.google?.accounts?.id;
        const host = buttonHostRef.current as unknown as HTMLElement | null;
        if (!gis || !host) {
          setErrorMessage('Gagal memuat tombol Google. Muat ulang halaman.');
          return;
        }
        gis.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => {
            void handleCredential(resp);
          },
        });
        // Kosongkan dulu supaya re-mount tidak menggandakan tombol.
        host.innerHTML = '';
        gis.renderButton(host, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          width: 280,
          locale: 'id',
        });
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMessage('Gagal memuat Google Sign-In. Periksa koneksi lalu muat ulang.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [routeAfterLogin]);

  return (
    <SafeAreaView style={styles.safe}>
      <SegeraHadirModal
        visible={signupBonus !== null}
        onClose={continueAfterSignup}
        onCtaPress={continueAfterSignup}
        title="Selamat! Kamu mendapatkan 50 point gratis"
        body={`Akun baru kamu sudah mendapat ${signupBonus?.amount ?? 50} point. Gunakan 1 point untuk memilih 1 worksheet. Point tidak kedaluwarsa.`}
        ctaLabel="Lanjut ke Funpaper"
      />
      <View style={styles.center}>
        <AppIcon source={MISC_ICON.sparkle} size={72} />
        <ThemedText type="title" style={styles.title}>
          Funpaper
        </ThemedText>
        <ThemedText type="default" themeColor="textSecondary" style={styles.body}>
          Lanjutkan dengan Google — gratis — untuk menyimpan progress dan
          aktivitas si kecil dengan aman di semua perangkat.
        </ThemedText>

        <View style={styles.buttonArea}>
          {Platform.OS !== 'web' ? (
            <ThemedText type="default" themeColor="textSecondary" style={styles.body}>
              Versi aplikasi belum tersedia. Buka funpaper.terusmengajar.id
              dari browser yah.
            </ThemedText>
          ) : (
            <>
              <ThemedText type="smallBold" style={styles.loginLabel}>
                Masuk dengan Google
              </ThemedText>
              {/* Host untuk GIS renderButton (DOM asli di dalam RNW View). */}
              <View ref={buttonHostRef} style={styles.gisHost} />
              {status !== 'ready' && !errorMessage ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator color={Brand.primary} />
                  <ThemedText type="default" themeColor="textSecondary">
                    {status === 'signing-in' ? 'Memproses login…' : 'Memuat…'}
                  </ThemedText>
                </View>
              ) : null}
            </>
          )}
          {errorMessage ? (
            <ThemedText type="default" style={styles.error}>
              {errorMessage}
            </ThemedText>
          ) : null}
        </View>

        <ThemedText type="default" themeColor="textSecondary" style={styles.legal}>
          Dengan masuk, kamu menyetujui Syarat & Ketentuan serta Kebijakan
          Privasi Funpaper.
        </ThemedText>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  title: {
    textAlign: 'center',
    color: Brand.primary,
  },
  body: {
    textAlign: 'center',
    maxWidth: 340,
  },
  buttonArea: {
    marginTop: Spacing.three,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.two,
  },
  gisHost: {
    minWidth: 280,
    minHeight: 44,
  },
  loginLabel: {
    color: Brand.primary,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  error: {
    color: '#b3261e',
    textAlign: 'center',
    maxWidth: 320,
  },
  legal: {
    marginTop: Spacing.four,
    textAlign: 'center',
    maxWidth: 320,
    fontSize: 12,
  },
});
